import { useRef, useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/AuthProvider';
import { useStableAvatar } from '@/hooks/use-stable-avatar';
import { Book } from '@/types/book';
import { offlineQueue } from '@/lib/offlineQueue';

// Single source of truth for reading progress
export interface BookProgress {
  bookId: number;
  currentPage: number;
  lastReadAt: string;
  hasProgress: boolean;
}

// Hook for tracking progress while reading (IMMEDIATE saves, no debouncing)
export function useReadingProgressTracker(bookId: number) {
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const queryClient = useQueryClient();
  const lastSavedPageRef = useRef<number>(0);
  const sessionStartRef = useRef<string | null>(null);

  // Initialize session start time
  if (session?.user?.id && currentProfile?.id && !sessionStartRef.current) {
    sessionStartRef.current = new Date().toISOString();
  }

  // Save progress immediately to database
  const saveProgress = useCallback(async (page: number) => {
    if (!session?.user?.id || !currentProfile?.id || page <= 0 || page === lastSavedPageRef.current) return;

    const now = new Date().toISOString();
    const startTime = sessionStartRef.current || now;

    try {
      console.log(`💾 Saving progress: Book ${bookId}, Page ${page}, Profile ${currentProfile.id}`);
      
      const { error } = await supabase
        .from('reading_sessions')
        .upsert({
          user_id: session.user.id,
          profile_id: currentProfile.id,
          book_id: bookId,
          current_page: page,
          start_time: startTime,
          end_time: now
        }, {
          onConflict: 'user_id,profile_id,book_id'
        });

      if (error) {
        console.log('⏸️ Offline - queueing progress update');
        await offlineQueue.queueReadingProgress({
          bookId,
          userId: session.user.id,
          profileId: currentProfile.id,
          currentPage: page,
        });
      } else {
        lastSavedPageRef.current = page;
        // Invalidate progress cache so other components get updated data
        queryClient.invalidateQueries({ queryKey: ['bookProgress'] });
        queryClient.invalidateQueries({ queryKey: ['profileReadingData'] });
      }
    } catch (error) {
      console.log('⏸️ Network error - queueing progress update');
      await offlineQueue.queueReadingProgress({
        bookId,
        userId: session.user.id,
        profileId: currentProfile.id,
        currentPage: page,
      });
    }
  }, [bookId, session?.user?.id, currentProfile?.id, queryClient]);

  const updateCurrentPage = useCallback((page: number) => {
    if (page > 0) {
      saveProgress(page);
    }
  }, [saveProgress]);

  return {
    updateCurrentPage,
    saveProgress: () => saveProgress(lastSavedPageRef.current)
  };
}

// Hook for getting progress for multiple books (React Query with cross-device sync)
export function useMultipleBookProgress(books: Book[]) {
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();
  const queryClient = useQueryClient();
  
  // Memoize book IDs to prevent unnecessary re-fetches
  const bookIds = useMemo(() => 
    books.map(book => Number(book.id)).sort((a, b) => a - b), 
    [books]
  );

  // Create a stable key for the book IDs
  const bookIdsKey = bookIds.join(',');

  const { data: progress = new Map(), isLoading } = useQuery({
    queryKey: ['bookProgress', session?.user?.id, currentProfile?.id, bookIdsKey],
    queryFn: async () => {
      if (!session?.user?.id || !currentProfile?.id || bookIds.length === 0) {
        return new Map<number, BookProgress>();
      }

      const { data: sessions, error } = await supabase
        .from('reading_sessions')
        .select('book_id, current_page, end_time, start_time')
        .eq('user_id', session.user.id)
        .eq('profile_id', currentProfile.id)
        .in('book_id', bookIds)
        .order('end_time', { ascending: false });

      if (error) {
        console.error('Error fetching reading progress:', error);
        return new Map<number, BookProgress>();
      }

      // Create progress map - only keep the most recent session per book
      const progressMap = new Map<number, BookProgress>();
      
      sessions?.forEach(session => {
        if (!progressMap.has(session.book_id)) {
          progressMap.set(session.book_id, {
            bookId: session.book_id,
            currentPage: session.current_page || 0,
            lastReadAt: session.end_time || session.start_time,
            hasProgress: (session.current_page || 0) > 0
          });
        }
      });

      return progressMap;
    },
    enabled: !!session?.user?.id && !!currentProfile?.id && bookIds.length > 0,
    staleTime: 30 * 1000, // 30 seconds - balance between freshness and performance
    refetchOnWindowFocus: true, // Sync when tab becomes active
    refetchOnReconnect: true, // Sync when coming back online
    refetchInterval: 60 * 1000, // Background refresh every 60 seconds
  });

  // Memoize the getter function
  const getBookProgress = useCallback((bookId: number) => 
    progress.get(bookId), [progress]);

  const refreshProgress = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['bookProgress'] });
  }, [queryClient]);

  return {
    getBookProgress,
    isLoading,
    refreshProgress
  };
}

// Hook for getting progress for a single book (React Query with cross-device sync)
export function useBookProgress(bookId: number) {
  const { session } = useAuth();
  const { currentProfile } = useStableAvatar();

  const { data: progress = null, isLoading } = useQuery({
    queryKey: ['singleBookProgress', session?.user?.id, currentProfile?.id, bookId],
    queryFn: async () => {
      if (!session?.user?.id || !currentProfile?.id || !bookId) {
        return null;
      }

      const { data, error } = await supabase
        .from('reading_sessions')
        .select('current_page, end_time, start_time')
        .eq('user_id', session.user.id)
        .eq('profile_id', currentProfile.id)
        .eq('book_id', bookId)
        .order('end_time', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') {
        console.error('Error fetching reading progress:', error);
        return null;
      }
      
      if (data) {
        return {
          bookId,
          currentPage: data.current_page || 0,
          lastReadAt: data.end_time || data.start_time,
          hasProgress: (data.current_page || 0) > 0
        } as BookProgress;
      }
      
      return null;
    },
    enabled: !!session?.user?.id && !!currentProfile?.id && !!bookId,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });

  return {
    progress,
    isLoading,
    hasProgress: progress?.hasProgress || false,
    currentPage: progress?.currentPage || 0
  };
}